import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTEREST_OPTIONS = [
  {
    key: "forras_kod",
    label: "Forrás-Kód® egyéni és csoportos folyamatok",
  },
  {
    key: "meditations",
    label: "Meditációk és új programok",
  },
];

/**
 * NewsletterSignup — Prémium, újrafelhasználható hírlevél-feliratkozási blokk.
 *
 * Használat: <NewsletterSignup source="homepage" />
 *
 * - Backend végpont: POST /api/newsletter/subscribe → MailerLite (double opt-in)
 * - A MAILERLITE_API_KEY és csoport-azonosítók beállításáig a backend
 *   503-at ad vissza, és NEM mutat hamis sikeres visszaigazolást.
 */
export default function NewsletterSignup({ source = "homepage", compact = false }) {
  const [form, setForm] = useState({
    firstName: "",
    email: "",
    interests: [],
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState("idle"); // idle | ok | pending | error
  const [notice, setNotice] = useState("");

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const toggleInterest = (key) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(key)
        ? f.interests.filter((k) => k !== key)
        : [...f.interests, key],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Add meg az e-mail-címed.");
      return;
    }
    if (form.interests.length === 0) {
      toast.error("Kérlek, jelölj be legalább egy érdeklődési kört.");
      return;
    }
    if (!form.consent) {
      toast.error("A hozzájárulás elfogadása szükséges a feliratkozáshoz.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, {
        firstName: form.firstName.trim(),
        email: form.email.trim(),
        interests: form.interests,
        consent: form.consent,
        source,
      });
      const isNew = res.data?.status === "pending_confirmation";
      setNotice(isNew ? "" : res.data.message);
      setState("pending");
      if (isNew) toast.success("Köszönöm! Elküldtem egy megerősítő e-mailt.");
      setForm({ firstName: "", email: "", interests: [], consent: false });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Valami nem sikerült. Kérlek, próbáld meg később.";
      setState("error");
      toast.error(typeof msg === "string" ? msg : "Hiba történt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="hirlevel"
      data-testid="newsletter-signup"
      className={`relative px-5 md:px-10 bg-tinted border-t border-hairline ${
        compact ? "py-10 md:py-12" : "py-12 md:py-16"
      }`}
    >
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center">
          <p className="text-[12px] tracking-[0.42em] uppercase text-gold mb-5 font-body">
            — Kapcsolódjunk!
          </p>
          <h2
            data-testid="newsletter-title"
            className="font-serif-display text-[32px] sm:text-[38px] md:text-[44px] leading-[1.18] text-graphite mb-4"
          >
            Értesülj{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              elsőként
            </em>{" "}
            az újdonságokról
          </h2>
          <p className="font-body font-light text-[15px] md:text-[16.5px] leading-[1.8] text-graphite max-w-lg mx-auto">
            Iratkozz fel hírlevelemre, és értesülj az új Forrás-Kód
            <sup className="text-gold">®</sup> folyamatokról, csoportos alkalmakról és
            induló programokról.
          </p>
        </div>

        {/* Form */}
        {state === "pending" ? (
          <div
            data-testid="newsletter-pending"
            className="mt-8 md:mt-10 border border-gold bg-warm px-7 md:px-10 py-10 md:py-12 text-center max-w-xl mx-auto"
            style={{ boxShadow: "0 10px 40px -24px rgba(160, 122, 40, 0.22)" }}
          >
            {notice ? (
              <p data-testid="newsletter-notice" className="font-serif-display text-graphite text-[20px] md:text-[22px] leading-[1.55]">
                {notice}
              </p>
            ) : (
              <>
                <p className="font-serif-display text-graphite text-[20px] md:text-[22px] leading-[1.55] mb-3">
                  Köszönöm! Elküldtem egy{" "}
                  <span className="text-gold">megerősítő</span> e-mailt.
                </p>
                <p className="font-body font-light text-graphite-soft text-[14.5px] md:text-[15px] leading-[1.7]">
                  A feliratkozás akkor válik véglegessé, amikor a levélben lévő megerősítő linkre
                  kattintasz. Ha nem találod, nézd meg a Promóciók vagy a Spam mappát is.
                </p>
              </>
            )}
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-testid="newsletter-form"
            className="mt-8 md:mt-10 bg-warm border border-hairline px-6 md:px-10 py-7 md:py-8"
          >
            {/* First name + email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
              <div>
                <label
                  htmlFor="newsletter-firstName"
                  className="block font-body text-[10.5px] md:text-[11px] tracking-[0.3em] uppercase text-muted-warm mb-1"
                >
                  Keresztnév
                </label>
                <input
                  id="newsletter-firstName"
                  name="firstName"
                  type="text"
                  value={form.firstName}
                  onChange={handleChange}
                  data-testid="newsletter-firstName"
                  className="input-underline !py-2 w-full"
                  autoComplete="given-name"
                  maxLength={100}
                />
              </div>
              <div>
                <label
                  htmlFor="newsletter-email"
                  className="block font-body text-[10.5px] md:text-[11px] tracking-[0.3em] uppercase text-muted-warm mb-1"
                >
                  E-mail-cím <span className="text-gold">*</span>
                </label>
                <input
                  id="newsletter-email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  data-testid="newsletter-email"
                  className="input-underline !py-2 w-full"
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Interest checkboxes */}
            <fieldset className="mt-6 md:mt-7">
              <legend className="block font-body text-[10.5px] md:text-[11px] tracking-[0.3em] uppercase text-muted-warm mb-2.5">
                Érdeklődési körök
              </legend>
              <div className="space-y-2">
                {INTEREST_OPTIONS.map((opt) => {
                  const checked = form.interests.includes(opt.key);
                  return (
                    <label
                      key={opt.key}
                      className="flex items-start gap-3 cursor-pointer group"
                      data-testid={`newsletter-interest-${opt.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleInterest(opt.key)}
                        className="mt-[5px] accent-gold flex-shrink-0"
                      />
                      <span
                        className={`font-body text-[14.5px] md:text-[15.5px] leading-[1.65] transition-colors duration-300 ${
                          checked ? "text-graphite" : "text-graphite-soft group-hover:text-graphite"
                        }`}
                      >
                        {opt.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {/* Consent */}
            <div className="mt-6 md:mt-7">
              <label
                className="flex items-start gap-3 cursor-pointer group"
                data-testid="newsletter-consent"
              >
                <input
                  type="checkbox"
                  name="consent"
                  checked={form.consent}
                  onChange={handleChange}
                  required
                  className="mt-[5px] accent-gold flex-shrink-0"
                />
                <span className="font-body font-light text-[13.5px] md:text-[14px] leading-[1.75] text-graphite">
                  Hozzájárulok, hogy a megadott nevem és e-mail-címem a hírlevél
                  küldésének céljából kerüljön kezelésre. Tudomásul veszem, hogy a
                  feliratkozás a visszaigazoló e-mailben lévő link megnyitásával válik
                  véglegessé, és bármikor leiratkozhatom. A részletes feltételeket az{" "}
                  <Link
                    to="/adatkezelesi-tajekoztato"
                    className="text-gold border-b border-gold hover:opacity-80 transition-opacity duration-300"
                  >
                    adatkezelési tájékoztató
                  </Link>{" "}
                  tartalmazza.
                </span>
              </label>
            </div>

            {/* Submit */}
            <div className="mt-6 md:mt-7 text-center">
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="cta-graphite inline-flex items-center justify-center gap-3 px-10 py-4 text-[12px] md:text-[13px] uppercase font-body font-medium tracking-[0.22em] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Küldés…" : "Feliratkozom"}
                {!loading && <span aria-hidden="true">→</span>}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
